import { Card, CardContent } from './ui/card';
import { cn } from '../lib/utils';
import { getQuotaAlertLevel } from '../lib/usageMetrics';
import { AlertTriangle, CircleAlert } from 'lucide-react';

/**
 * Remaining (primary) + total available (muted), with &lt;10% / ≤30% remaining alerts (same rules as dashboard).
 */
export function QuotaStatCard({
  title,
  icon: Icon,
  iconWrapClass,
  remaining,
  total,
  formatPrimary,
  formatTotalLine,
  testId,
  compact = false,
}) {
  const level = getQuotaAlertLevel(remaining, total);
  const line =
    level === 'critical'
      ? {
          text: 'Less than 10% remains — top up soon.',
          wrap: 'bg-red-50 text-red-800 border border-red-100',
          Flag: CircleAlert,
        }
      : level === 'warning'
        ? {
            text: 'Less than 30% of your allowance remains.',
            wrap: 'bg-amber-50 text-amber-900 border border-amber-100',
            Flag: AlertTriangle,
          }
        : null;

  return (
    <Card
      className={cn(
        'border-0 transition-shadow',
        compact ? 'shadow-sm hover:shadow-md' : 'shadow-md hover:shadow-lg',
        level === 'critical' && 'ring-2 ring-red-200/80',
        level === 'warning' && 'ring-2 ring-amber-200/80'
      )}
      data-testid={testId}
    >
      <CardContent className={cn(compact ? 'p-3' : 'p-6')}>
        <div className={cn('flex items-center justify-between gap-2', compact ? 'mb-2' : 'mb-4')}>
          <div
            className={cn(
              'flex items-center justify-center shrink-0',
              compact ? 'w-8 h-8 rounded-lg' : 'w-12 h-12 rounded-xl',
              iconWrapClass
            )}
          >
            <Icon className={compact ? 'w-4 h-4' : 'w-6 h-6'} />
          </div>
          {level ? (
            <span
              className={cn(
                'font-semibold rounded-full shrink-0',
                compact ? 'text-[10px] px-1.5 py-0.5' : 'text-xs px-2 py-1',
                level === 'critical' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'
              )}
            >
              {level === 'critical' ? 'Attention' : 'Warning'}
            </span>
          ) : null}
        </div>
        <p className={cn('text-slate-500', compact ? 'text-xs' : 'text-sm')}>{title}</p>
        <p
          className={cn(
            'font-jakarta font-bold text-slate-900 tabular-nums',
            compact ? 'text-xl mt-0.5' : 'text-3xl mt-1'
          )}
        >
          {formatPrimary(remaining)}
        </p>
        <p className={cn('text-slate-400 tabular-nums', compact ? 'text-[11px] mt-0.5' : 'text-xs mt-1')}>
          {formatTotalLine(total)}
        </p>
        {line ? (
          <div
            className={cn(
              'flex items-start gap-1.5 rounded-md font-medium',
              compact ? 'mt-2 px-2 py-1.5 text-[11px]' : 'mt-3 px-3 py-2 text-xs',
              line.wrap
            )}
            role="status"
          >
            <line.Flag className={cn('shrink-0 mt-0.5', compact ? 'w-3.5 h-3.5' : 'w-4 h-4')} aria-hidden />
            <span>{line.text}</span>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
